// @ts-nocheck

import { supabase } from '@/lib/supabase';

export async function createDefaultReferralLink(affiliateId: string, username?: string) {
  const base = (username || affiliateId).toString().replace(/[^A-Za-z0-9]/g, '');
  const code = (`${base}`.slice(0, 20) + 'REF').toUpperCase();
  const url = `${window.location.origin}/?ref=${code}`;

  const { data, error } = await supabase
    .from('affiliate_referral_links')
    .insert({
      affiliate_id: affiliateId,
      name: 'My referral link',
      url,
      code,
      description: 'Auto-generated default referral link',
      is_active: true,
    })
    .select()
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function generateAffiliateQRCode(affiliateId: string, name: string, linkUrl: string) {
  try {
    console.log('Generating QR code for affiliate:', affiliateId, 'with URL:', linkUrl);
    
    // Create QR code data (in a real implementation, this would use a QR code generator)
    // For now we'll use a placeholder URL that would generate a QR code
    const qrData = linkUrl;
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(linkUrl)}&size=200x200`;
    
    // Save to Supabase
    const { data, error } = await supabase
      .from('affiliate_qr_codes')
      .insert({
        affiliate_id: affiliateId,
        name,
        qr_data: qrData,
        qr_image_url: qrImageUrl,
        link_url: linkUrl,
        is_active: true
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error inserting QR code:', error);
      throw error;
    }
    
    console.log('QR code created successfully:', data);
    return data;
    
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw error;
  }
}

export async function generateAffiliateReferralLink(affiliateProfileId: string, name: string, description: string = '') {
  try {
    console.log('Generating referral link for affiliate profile ID:', affiliateProfileId);
    
    // Get the current referral code from affiliates table (single source of truth)
    const { data: affiliateData, error: affiliateError } = await supabase
      .from('affiliates')
      .select('referral_code')
      .eq('user_id', affiliateProfileId)
      .single();
      
    if (affiliateError) {
      console.error('Error fetching affiliate referral code:', affiliateError);
      throw affiliateError;
    }
    
    if (!affiliateData?.referral_code) {
      throw new Error('No referral code found for affiliate');
    }
    
    const referralCode = affiliateData.referral_code;
    console.log('Using referral code:', referralCode);
    
    // Generate the dynamic URL using the recruitlook.com domain
    const url = `https://recruitlook.com/register?ref=${referralCode}`;
    
    console.log('Creating referral link with URL:', url);
    
    // Save the referral link for analytics/legacy purposes
    const { data, error } = await supabase
      .from('affiliate_referral_links')
      .insert({
        affiliate_id: affiliateProfileId,
        name,
        url,
        description,
        is_active: true
      })
      .select()
      .single();
      
    if (error) {
      console.error('Error inserting referral link:', error);
      throw error;
    }
    
    console.log('Referral link created successfully:', data);
    return data;
    
  } catch (error) {
    console.error('Error generating referral link:', error);
    throw error;
  }
}

export async function syncLegacyTablesOnReferralCodeChange(affiliateUserId: string, newReferralCode: string) {
  try {
    console.log('Syncing legacy tables for affiliate:', affiliateUserId, 'with new code:', newReferralCode);
    
    // Update affiliate_referral_links with new URLs
    const { data: existingLinks } = await supabase
      .from('affiliate_referral_links')
      .select('*')
      .eq('affiliate_id', affiliateUserId);
      
    if (existingLinks && existingLinks.length > 0) {
      for (const link of existingLinks) {
        // Extract the path from the old URL and rebuild with new referral code
        const oldUrl = new URL(link.url);
        const path = oldUrl.pathname;
        const newUrl = `https://recruitlook.com${path}?ref=${newReferralCode}`;
        
        await supabase
          .from('affiliate_referral_links')
          .update({ url: newUrl })
          .eq('id', link.id);
      }
    }
    
    // Update affiliate_qr_codes with new URLs
    const { data: existingQRCodes } = await supabase
      .from('affiliate_qr_codes')
      .select('*')
      .eq('affiliate_id', affiliateUserId);
      
    if (existingQRCodes && existingQRCodes.length > 0) {
      for (const qrCode of existingQRCodes) {
        // Extract the path from the old URL and rebuild with new referral code
        const oldUrl = new URL(qrCode.link_url);
        const path = oldUrl.pathname;
        const newUrl = `https://recruitlook.com${path}?ref=${newReferralCode}`;
        const newQRImageUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(newUrl)}&size=200x200`;
        
        await supabase
          .from('affiliate_qr_codes')
          .update({ 
            link_url: newUrl,
            qr_data: newUrl,
            qr_image_url: newQRImageUrl
          })
          .eq('id', qrCode.id);
      }
    }
    
    console.log('Legacy tables synced successfully');
    return true;
    
  } catch (error) {
    console.error('Error syncing legacy tables:', error);
    return false;
  }
}

export async function trackAffiliateClick(linkId: string) {
  try {
    // Increment the click count for this link
    const { error } = await supabase
      .from('affiliate_referral_links')
      .update({ clicks: supabase.rpc('increment', { x: 1 }) })
      .eq('id', linkId);
      
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error tracking click:', error);
    return false;
  }
}

export async function trackAffiliateConversion(linkId: string) {
  try {
    // Increment the conversion count for this link
    const { error } = await supabase
      .from('affiliate_referral_links')
      .update({ conversions: supabase.rpc('increment', { x: 1 }) })
      .eq('id', linkId);
      
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error tracking conversion:', error);
    return false;
  }
}
